import React, { useCallback, useEffect, useRef, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK ?? "";

type PageMode = "AddUserForFirstTime" | "AddUser" | "ManageUser";

interface UserRow {
  rowNo:  number;
  login:  string;
  fname:  string;
  gender: string;
  icon:   string;
}

interface CompetencyOption {
  value: string;
  label: string;
}

interface PageData {
  heading:          string;
  userTip:          string;
  strListHeading:   string;
  familyName:       string;
  pageMode:         PageMode;
  maxChildren:      number;
  availChildren:    number;
  userList:         UserRow[];
  stdOptions:       string[];
  ddlCurriculum:    string[];
  ddlCompetency:    CompetencyOption[];
  displaytr:        string;   // "none" | ""
  homeSchoolLink:   string;   // "none" | ""
  getsDefaultLessonPlan:         string;
  getsDefaultLessonPlan_English: string;
  // Pipe-separated arrays (mirrors VB hidden fields)
  logins:     string;
  fnames:     string;
  lnames:     string;
  standard:   string;
  genders:    string;
  emails:     string;
  dobs:       string;
  competency: string;
}

function showMessage(msg: string) {
  if (msg && msg.trim()) alert(msg);
}

function gfn_Trim(s: string) { return (s || "").trim(); }

const css = `
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; padding: 0; }

  #tblContent {
    width: 100%;
    border-collapse: collapse;
    font-family: Arial;
    font-weight: bold;
  }

  #trHead td {
    color: #568EB3;
    font-size: 16pt;
    height: 40px;
    vertical-align: middle;
  }

  /* Left form panel */
  .form-panel {
    width: 75%;
    vertical-align: top;
    padding-right: 5px;
  }

  /* Right user list panel */
  .list-panel {
    width: 25%;
    vertical-align: top;
  }

  /* Inner form table */
  .inner-table {
    width: 100%;
    font-family: Arial;
    height: 265px;
    vertical-align: top;
    border-collapse: collapse;
  }

  /* Tip row */
  .tip-cell {
    color: #254967;
    border: 1px solid #CCC;
    font-size: 11pt;
    padding: 10px;
    text-align: center;
    vertical-align: middle;
    height: 50px;
  }

  .tip-note {
    font-size: 9pt;
    color: #FF9900;
  }

  /* Form rows */
  .form-row {
    height: 30px;
  }

  .label-cell {
    width: 25%;
    padding-right: 10px;
    font-size: 11pt;
    font-weight: bold;
    color: #254967;
    border-left: 1px solid #CCC;
    text-align: right;
  }

  .field-cell {
    width: 75%;
    text-align: left;
    border-right: 1px solid #CCC;
  }

  .spacer-cell {
    height: 10px;
    border-left: 1px solid #CCC;
    border-right: 1px solid #CCC;
    font-size: 1pt;
  }

  .bottom-border-cell {
    height: 10px;
    border-left: 1px solid #CCC;
    border-right: 1px solid #CCC;
    border-bottom: 1px solid #CCC;
    font-size: 1pt;
  }

  /* cssTextBox */
  .cssTextBox {
    font-family: 'Trebuchet MS', sans-serif;
    font-size: 11pt;
    border: 1px solid #006600;
    padding-left: 5px;
    width: 96%;
  }

  .cssTextBox-sm {
    font-family: 'Trebuchet MS', sans-serif;
    font-size: 11pt;
    border: 1px solid #006600;
    padding-left: 5px;
    width: 80px;
  }

  /* User list */
  #tdUserList {
    border: 1px solid #CCC;
    vertical-align: top;
    height: 280px;
  }

  #divUserList {
    overflow-y: scroll;
    overflow-x: hidden;
    height: 100%;
  }

  .user-row {
    font-size: 10pt;
    font-family: Arial;
    font-weight: bold;
    color: #568EB3;
    cursor: pointer;
  }

  .user-row:hover {
    background-color: #e8f0f8;
  }

  .user-row.selected {
    background-color: blue;
    color: white;
  }

  .user-row td {
    border-bottom: 1px solid #CCC;
    padding: 5px;
    vertical-align: middle;
  }

  .user-row td.icon-cell {
    width: 10%;
    text-align: center;
    white-space: nowrap;
  }

  .user-row td.name-cell {
    width: 90%;
    padding-left: 5px;
  }

  /* Buttons */
  .btn-submit-img {
    cursor: pointer;
    height: 28px;
    margin-right: 8px;
    border: none;
    background: none;
    padding: 0;
  }

  .btn-clear-img {
    cursor: pointer;
    height: 28px;
    border: none;
    background: none;
    padding: 0;
  }

  /* Action rows */
  #trAddFirstUserButtons td {
    padding: 5px 0 0 0;
    height: 40px;
    text-align: center;
  }

  #trManageUserButtons td {
    padding-top: 15px;
    text-align: center;
    vertical-align: top;
  }

  /* Flash image */
  #divFlash img {
    width: 150px;
    height: 150px;
    border: none;
  }

  /* HomeSchool link */
  #LinkHomeAndSchool {
    color: #0000EE;
    text-decoration: underline;
    cursor: pointer;
  }

  /* Loading */
  .loading-msg {
    padding: 20px;
    font-family: Arial;
    font-size: 11pt;
    color: #555;
  }

  .error-msg {
    color: red;
    font-family: Arial;
    font-size: 9pt;
    display: none;
  }
`;

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ELManageFamilyUsers() {

  // mirrors: hidPage.Value = Request.QueryString("Option")
  const qp       = new URLSearchParams(window.location.search);
  const pageMode = (qp.get("Option") ?? "ManageUser") as PageMode;

  // ── Data state ───────────────────────────────────────────────────────────────
  const [pageData,    setPageData]    = useState<PageData | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [loginError,  setLoginError]  = useState("");
  const [message,     setMessage]     = useState("");

  // ── Form state — mirrors all asp:TextBox / DropDownList controls ─────────────
  const [fname,       setFname]       = useState("");
  const [lname,       setLname]       = useState("");  // txtLName — defaults to FamilyName
  const [email,       setEmail]       = useState("");
  const [std,         setStd]         = useState("-1");
  const [curriculum,  setCurriculum]  = useState("-1");
  const [competency,  setCompetency]  = useState("3");
  const [genderMale,  setGenderMale]  = useState(false);
  const [genderFem,   setGenderFem]   = useState(false);
  const [dobEnabled,  setDobEnabled]  = useState(false);  // chkDOB
  const [dob,         setDob]         = useState("");     // calDOB

  // ── Selection state — mirrors SelRow / hidSelUser ────────────────────────────
  const [selLogin,    setSelLogin]    = useState("");
  const [selFname,    setSelFname]    = useState("");
  const [selIndex,    setSelIndex]    = useState(-1);

  // ── Submit state ──────────────────────────────────────────────────────────────
  const [submitting, setSubmitting]  = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const fnameRef = useRef<HTMLInputElement>(null);

  // ── Load page data — mirrors Page_Load ───────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}api/manage-family-users/?Option=${pageMode}`)
      .then(r => r.json())
      .then((d: PageData) => {
        setPageData(d);
        // mirrors: txtLName.Text = FamilyName
        setLname(d.familyName);
        // mirrors: gfn_ShowMessage('<% =LoginError%>') / gfn_ShowMessage("<% =Message%>")
        if (d.heading) { /* heading set */ }
      })
      .catch(() => setLoginError("Failed to load page."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── fn_ClearFields — mirrors inline JS fn_ClearFields() ─────────────────────
  const fn_ClearFields = useCallback(() => {
    setFname("");
    setLname(pageData?.familyName ?? "");
    setEmail("");
    setStd("-1");
    setCurriculum("-1");
    setGenderMale(false);
    setGenderFem(false);
    setDobEnabled(false);
    setDob("");
    setCompetency("3");
    // mirrors: txtFname.Focus()
    setTimeout(() => fnameRef.current?.focus(), 50);
    return false;
  }, [pageData]);

  // ── fn_PopulateFields — mirrors inline JS fn_PopulateFields() ───────────────
  // Called when a row in the user list is clicked (ManageUser mode)
  const fn_PopulateFields = useCallback((login: string, idx: number) => {
    if (!pageData) return;
    const logins    = pageData.logins.split("|");
    const fnames    = pageData.fnames.split("|");
    const lnames    = pageData.lnames.split("|");
    const stds      = pageData.standard.split("|");
    const genders   = pageData.genders.split("|");
    const emails    = pageData.emails.split("|");
    const dobs      = pageData.dobs.split("|");
    const comps     = pageData.competency.split("|");

    for (let i = 0; i < logins.length; i++) {
      if (login === logins[i]) {
        setFname(fnames[i]  ?? "");
        setLname(lnames[i]  !== "" ? lnames[i] : pageData.familyName);
        setEmail(emails[i]  !== " " ? emails[i] : "");
        setStd(stds[i]      === "" || stds[i] === "-1" ? "-1" : stds[i]);
        const g = genders[i] ?? "";
        setGenderMale(g === "B");
        setGenderFem(g  === "G");
        const d = dobs[i] ?? "";
        if (d.trim() !== "" && d.trim() !== " ") {
          setDobEnabled(true);
          setDob(d.trim());
        } else {
          setDobEnabled(false);
          setDob("");
        }
        const comp = comps[i] ?? "1";
        for (let j = 0; j < (pageData.ddlCompetency?.length ?? 0); j++) {
          if (pageData.ddlCompetency[j].value === comp) {
            setCompetency(comp);
            break;
          }
        }
        setSelIndex(i);
        break;
      }
    }
  }, [pageData]);

  // ── fn_SetSelRow — mirrors inline JS fn_SetSelRow(pObj) ─────────────────────
  const fn_SetSelRow = useCallback((row: UserRow) => {
    if (pageMode === "AddUser") return;
    setSelLogin(row.login);
    setSelFname(row.fname);
    if (pageMode === "ManageUser") {
      const idx = pageData?.userList.findIndex(u => u.login === row.login) ?? -1;
      fn_PopulateFields(row.login, idx);
    }
  }, [pageMode, pageData, fn_PopulateFields]);

  // ── fn_ValidateLogin — mirrors inline JS fn_ValidateLogin() ─────────────────
  function fn_ValidateLogin(): boolean {
    const x   = fname.length;
    const lid = fname;
    if (!lid) {
      showMessage("Enter Firstname");
      return false;
    }
    if (x < 2) {
      showMessage("First name must be minimum 2 characters");
      fnameRef.current?.focus();
      return false;
    }
    for (let i = 0; i < x; i++) {
      const ch = lid.charAt(i).toUpperCase();
      if (!((ch >= "A" && ch <= "Z") || ch === "'" || ch === " ")) {
        showMessage("First name must contain letters only. Please try again.");
        return false;
      }
    }
    return true;
  }

  // ── fn_CheckMaxChildren — mirrors inline JS fn_CheckMaxChildren() ────────────
  function fn_CheckMaxChildren(): boolean {
    const avail = pageData?.availChildren ?? 0;
    const max   = pageData?.maxChildren   ?? 1;
    const mx    = max === 0 ? 1 : max;
    if (avail < mx) return true;
    showMessage(`You have reached your maximum children limit of ${mx}.\nTo add more children, contact your provider.`);
    return false;
  }

  // ── fn_ValidateSubmit — mirrors inline JS fn_ValidateSubmit(pObj) ────────────
  async function fn_ValidateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    // mirrors: If hidPage.Value == 'ManageUser' && SelectedChildName == '' → error
    if (pageMode === "ManageUser" && selFname === "") {
      showMessage("Please select a student");
      return;
    }

    // mirrors: GUEST check
    const un = gfn_Trim(selFname).toUpperCase();
    if (pageMode === "ManageUser" && un === "GUEST") {
      showMessage("Guest account cannot be modified\nPlease contact your supplier if you need an additional user.");
      return;
    }
    if (un === "GUEST" && gfn_Trim(fname).toUpperCase() !== "GUEST") {
      showMessage("Guest account cannot be modified\nPlease contact your supplier if you need an additional user.");
      return;
    }

    // mirrors: fn_CheckMaxChildren (only for AddUser / AddUserForFirstTime)
    if (pageMode !== "ManageUser") {
      if (!fn_CheckMaxChildren()) return;
    }

    if (!fn_ValidateLogin()) return;

    // mirrors: if rdoMale && rdoFemale both unchecked → nothing (no error)
    // mirrors: if ddlStandard.selectedIndex <= 0 → error
    if (std === "-1" || std === "") {
      showMessage("Please select Grade/Year");
      return;
    }

    // mirrors: if ddlCurriculum.selectedIndex <= 0 → error
    if (curriculum === "-1" || curriculum === "" || curriculum === "--Select--") {
      showMessage("Please select curriculum");
      return;
    }

    // ── Check editability (ManageUser mode with Std or Competency change) ───────
    // mirrors: fn_ValidateSubmit calls gfn_GetURLContent("EL_ManageFamilyUsers.aspx?Process=CheclEditability&...")
    if (pageMode === "ManageUser" && pageData?.getsDefaultLessonPlan) {
      const stdArr   = pageData.standard.split("|");
      const compArr  = pageData.competency.split("|");
      const prevStd  = stdArr[selIndex]  ?? "";
      const prevComp = compArr[selIndex] ?? "";

      if (std !== prevStd || competency !== prevComp) {
        const url = `${API_BASE_URL}api/manage-family-users/check-editability/`
          + `?Login=${selLogin}&StdNew=${std}&StdOld=${prevStd}`
          + `&CompetencyNew=${competency}&CompetencyOld=${prevComp}`;
        try {
          const resp = await fetch(url);
          const text = await resp.text();
          const i    = text.indexOf("----------!#$*()-_+=----------");
          const ret  = i >= 0 ? text.substring(0, i) : text;
          const cd   = ret.charAt(0);
          const mesg = ret.substring(2);

          if (cd === "0") {
            // OK — proceed
          } else if (cd === "1") {
            if (!window.confirm(mesg)) return;
          } else {
            showMessage(mesg);
            return;
          }
        } catch {
          showMessage("Network error checking editability.");
          return;
        }
      }
    }

    // ── Submit ────────────────────────────────────────────────────────────────
    setSubmitting(true);
    try {
      if (pageMode === "ManageUser") {
        await doUpdate();
      } else {
        await doAddUser();
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── doAddUser — mirrors imgSubmit_Click → fn_AddUser() ───────────────────────
  async function doAddUser() {
    const payload = {
      fname, lname, gender: genderMale ? "B" : genderFem ? "G" : "",
      std, curriculum, dob: dobEnabled ? dob : "",
      email, competency,
      getsDefaultLessonPlan:         pageData?.getsDefaultLessonPlan         ?? "",
      getsDefaultLessonPlan_English: pageData?.getsDefaultLessonPlan_English ?? "",
    };

    const resp   = await fetch(`${API_BASE_URL}api/manage-family-users/add-user/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await resp.json();

    if (result.success) {
      setMessage("User added successfully.");
      fn_ClearFields();
      // mirrors: fn_GetChildrenOfFamily() refresh
      refreshData();
    } else {
      showMessage(result.error || "Error adding user.");
    }
  }

  // ── doUpdate — mirrors imgSubmit_Click → fn_Update() ─────────────────────────
  async function doUpdate() {
    const payload = {
      login: selLogin,
      fname, lname, email,
      gender: genderMale ? "B" : genderFem ? "G" : "",
      std, dob: dobEnabled ? dob : "",
      competency,
      getsDefaultLessonPlan:         pageData?.getsDefaultLessonPlan         ?? "",
      getsDefaultLessonPlan_English: pageData?.getsDefaultLessonPlan_English ?? "",
    };

    const resp   = await fetch(`${API_BASE_URL}api/manage-family-users/update-user/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await resp.json();

    if (result.success) {
      setMessage("User updated successfully.");
      fn_ClearFields();
      refreshData();
    } else {
      showMessage(result.error || "Error updating user.");
    }
  }

  // ── Refresh data after add/update ─────────────────────────────────────────────
  function refreshData() {
    fetch(`${API_BASE_URL}api/manage-family-users/users/`)
      .then(r => r.json())
      .then((d) => {
        if (pageData) {
          setPageData(prev => prev ? {
            ...prev,
            availChildren: d.availChildren,
            userList:      d.userList,
            logins:        d.logins,
            fnames:        d.fnames,
            lnames:        d.lnames,
            standard:      d.standard,
            genders:       d.genders,
            emails:        d.emails,
            dobs:          d.dobs,
            competency:    d.competency,
          } : null);
        }
      })
      .catch(() => {});
  }

  // ── fn_Continue — mirrors Master.Continue_Clicked ────────────────────────────
  async function fn_Continue() {
    if (!selLogin) {
      showMessage("Please select a student.");
      return;
    }
    const resp   = await fetch(`${API_BASE_URL}api/manage-family-users/login-as-student/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login: selLogin }),
    });
    const result = await resp.json();
    if (result.success) {
      window.location.href = result.redirect;
    } else {
      showMessage(result.error || "Login failed.");
    }
  }

  // ── fn_Link — mirrors inline JS fn_Link() → gfn_Popup ───────────────────────
  function fn_Link() {
    if (!selLogin) {
      showMessage("Please select a student.");
      return;
    }
    const url = `EL_LinkHomeAndSchoolLogins?L=${selLogin}`;
    // mirrors: gfn_Popup(URL, "Link Home and School Logins", "")
    window.open(url, "LinkHomeAndSchool", "width=600,height=400,resizable=yes");
  }

  // ── fn_EnableDisableDOB — mirrors inline JS ───────────────────────────────────
  function fn_EnableDisableDOB(checked: boolean) {
    setDobEnabled(checked);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <style>{css}</style>
      <div className="loading-msg">Loading...</div>
    </>
  );

  if (!pageData) return (
    <>
      <style>{css}</style>
      <div className="loading-msg" style={{ color: "red" }}>
        {loginError || "Failed to load page."}
      </div>
    </>
  );

  const showAddFirstButtons  = pageMode === "AddUserForFirstTime";
  const showManageButtons    = pageMode === "ManageUser" || pageMode === "AddUser";
  const showFlash            = pageMode === "AddUserForFirstTime";
  const showHomeSchoolLink   = pageData.homeSchoolLink !== "none";
  const showCompetencyRow    = pageData.displaytr !== "none";

  return (
    <>
      <style>{css}</style>

      {/* mirrors: gfn_ShowMessage('<% =LoginError%>') + gfn_ShowMessage("<% =Message%>") */}
      {loginError && <span style={{ display: "none" }} ref={el => { if (el && loginError) showMessage(loginError); }} />}
      {message    && <span style={{ display: "none" }} ref={el => { if (el && message)    showMessage(message);    }} />}

      <form onSubmit={fn_ValidateSubmit}>
        {/* mirrors: <table id="tblContent" width="100%"> */}
        <table id="tblContent" style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial", fontWeight: "bold" }}>
          <tbody>
            {/* mirrors: <tr id="trHead"> */}
            <tr id="trHead">
              <td width="75%" style={{ color: "#568EB3", fontSize: "16pt", height: "40px", verticalAlign: "middle" }}>
                {/* mirrors: <%=Server.HtmlEncode(Heading)%> */}
                {pageData.heading}
              </td>
              <td width="25%" style={{ color: "#568EB3", fontSize: "16pt", verticalAlign: "middle" }}>
                {/* mirrors: <%=Server.HtmlEncode(strListHeading)%> */}
                {pageData.strListHeading}
              </td>
            </tr>

            {/* mirrors: <tr id="trContent"> */}
            <tr id="trContent">
              {/* ── LEFT: Form ────────────────────────────────────────────── */}
              <td width="75%" valign="top" style={{ paddingRight: 5 }}>
                <table width="100%" style={{ fontFamily: "Arial", height: 265, verticalAlign: "top" }} cellSpacing={0}>
                  <tbody>

                    {/* Tip row */}
                    <tr>
                      <td colSpan={2} className="tip-cell">
                        {/* mirrors: <%=Server.HtmlEncode(UserTip)%> */}
                        {pageData.userTip}<br />
                        <span className="tip-note">Note: Fields with asterisk(*) are mandatory.</span>
                      </td>
                    </tr>
                    <tr><td colSpan={2} className="spacer-cell">&nbsp;</td></tr>

                    {/* First Name — mirrors: txtFname */}
                    <tr className="form-row">
                      <td className="label-cell">*&nbsp;First Name:</td>
                      <td className="field-cell">
                        <input
                          ref={fnameRef}
                          id="txtFname"
                          type="text"
                          className="cssTextBox"
                          maxLength={30}
                          value={fname}
                          onChange={e => setFname(e.target.value)}
                        />
                      </td>
                    </tr>

                    {/* Surname — mirrors: txtLName */}
                    <tr className="form-row">
                      <td className="label-cell">&nbsp;Surname:</td>
                      <td className="field-cell">
                        <input
                          id="txtLName"
                          type="text"
                          className="cssTextBox"
                          maxLength={30}
                          value={lname}
                          onChange={e => setLname(e.target.value)}
                        />
                      </td>
                    </tr>

                    {/* Grade/Year — mirrors: ddlStandard */}
                    <tr className="form-row">
                      <td className="label-cell">*&nbsp;Grade/Year:</td>
                      <td className="field-cell">
                        <select
                          id="ddlStandard"
                          className="cssTextBox-sm"
                          value={std}
                          onChange={e => setStd(e.target.value)}
                        >
                          <option value="-1">&lt;Select&gt;</option>
                          {pageData.stdOptions.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {/* Curriculum — mirrors: ddlCurriculum */}
                    <tr className="form-row">
                      <td className="label-cell">*&nbsp;Curriculum:</td>
                      <td className="field-cell">
                        <select
                          id="ddlCurriculum"
                          className="cssTextBox-sm"
                          value={curriculum}
                          onChange={e => setCurriculum(e.target.value)}
                        >
                          <option value="-1">&lt;Select&gt;</option>
                          {pageData.ddlCurriculum
                            .filter(c => c !== "--Select--")
                            .map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))
                          }
                        </select>
                      </td>
                    </tr>

                    {/* Gender — mirrors: rdoMale / rdoFemale */}
                    <tr className="form-row">
                      <td className="label-cell">Gender:</td>
                      <td className="field-cell">
                        <input
                          type="radio"
                          id="rdoMale"
                          name="rdoGender"
                          checked={genderMale}
                          onChange={() => { setGenderMale(true); setGenderFem(false); }}
                        />
                        <label htmlFor="rdoMale" style={{ fontFamily: "Arial", fontSize: "10pt", color: "black", marginLeft: 2 }}>Male</label>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        <input
                          type="radio"
                          id="rdoFemale"
                          name="rdoGender"
                          checked={genderFem}
                          onChange={() => { setGenderMale(false); setGenderFem(true); }}
                        />
                        <label htmlFor="rdoFemale" style={{ fontFamily: "Arial", fontSize: "10pt", color: "black", marginLeft: 2 }}>Female</label>
                      </td>
                    </tr>

                    {/* DOB — mirrors: chkDOB + calDOB + txtEmail (hidden) */}
                    <tr className="form-row">
                      <td className="label-cell">
                        {/* mirrors: <input type="checkbox" id="chkDOB" onclick="fn_EnableDisableDOB()"> */}
                        <input
                          type="checkbox"
                          id="chkDOB"
                          checked={dobEnabled}
                          onChange={e => fn_EnableDisableDOB(e.target.checked)}
                        />
                        Date of Birth:
                      </td>
                      <td className="field-cell" style={{ padding: 2 }}>
                        {/* mirrors: <span id="spnDOB" style='visibility: hidden/visible'> */}
                        <span id="spnDOB" style={{ visibility: dobEnabled ? "visible" : "hidden" }}>
                          {/* mirrors: <Calender:CAL ID="calDOB" /> */}
                          <input
                            type="date"
                            id="calDOB"
                            style={{ height: 30, fontFamily: "Arial", fontSize: "11pt", width: 80 }}
                            value={dob}
                            onChange={e => setDob(e.target.value)}
                          />
                          {/* mirrors: txtEmail hidden inside spnDOB (used for email storage) */}
                          <input
                            type="text"
                            id="txtEmail"
                            className="cssTextBox"
                            maxLength={200}
                            style={{ display: "none" }}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                          />
                        </span>
                      </td>
                    </tr>

                    {/* Competency — mirrors: Displaytr = "none" | "" */}
                    {showCompetencyRow && (
                      <tr className="form-row" style={{ display: showCompetencyRow ? undefined : "none" }}>
                        <td className="label-cell">Competency:</td>
                        <td className="field-cell">
                          <select
                            id="ddlCompetency"
                            className="cssTextBox"
                            value={competency}
                            onChange={e => setCompetency(e.target.value)}
                            style={{ width: "98%" }}
                          >
                            {pageData.ddlCompetency.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    )}

                    {/* HomeSchool link — mirrors: style="display: <%=HomeSchoolLink%>" */}
                    {showHomeSchoolLink && (
                      <tr className="form-row">
                        <td colSpan={2} style={{ borderLeft: "1px solid #CCC", borderRight: "1px solid #CCC" }}>
                          {/* mirrors: <a id="LinkHomeAndSchool" href="javascript: fn_Link()"> */}
                          <a
                            id="LinkHomeAndSchool"
                            href="#"
                            onClick={e => { e.preventDefault(); fn_Link(); }}
                          >
                            Click here to link your login account given in school
                          </a>
                        </td>
                      </tr>
                    )}

                    <tr><td colSpan={2} className="bottom-border-cell">&nbsp;</td></tr>

                    {/* AddFirstUser buttons — mirrors: trAddFirstUserButtons */}
                    {showAddFirstButtons && (
                      <tr id="trAddFirstUserButtons" style={{ height: 40, verticalAlign: "top" }}>
                        <td colSpan={2} style={{ padding: "5px 0 0 0", height: 40 }}>
                          <table width="100%" style={{ height: "100%" }}>
                            <tbody>
                              <tr>
                                <td align="center" style={{ padding: "5px 0 0 0", verticalAlign: "top" }}>
                                  {/* mirrors: imgSubmit */}
                                  <button
                                    type="submit"
                                    className="btn-submit-img"
                                    disabled={submitting}
                                    title="Submit"
                                  >
                                    <img
                                      src="/Images/submit_n.gif"
                                      alt="Submit"
                                      onMouseOver={e => (e.currentTarget.src = "/Images/submit_o.gif")}
                                      onMouseOut={e  => (e.currentTarget.src = "/Images/submit_n.gif")}
                                      onMouseDown={e => (e.currentTarget.src = "/Images/submit_d.gif")}
                                    />
                                  </button>
                                  &nbsp;&nbsp;
                                  {/* mirrors: imgClear */}
                                  <button
                                    type="button"
                                    className="btn-clear-img"
                                    onClick={fn_ClearFields}
                                    title="Clear"
                                  >
                                    <img
                                      src="/Images/clear_n.gif"
                                      alt="Clear"
                                      onMouseOver={e => (e.currentTarget.src = "/Images/clear_o.gif")}
                                      onMouseOut={e  => (e.currentTarget.src = "/Images/clear_n.gif")}
                                      onMouseDown={e => (e.currentTarget.src = "/Images/clear_d.gif")}
                                    />
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}

                  </tbody>
                </table>
              </td>

              {/* ── RIGHT: User list ──────────────────────────────────────── */}
              <td width="25%" valign="top">
                <table width="100%" cellPadding={0} cellSpacing={0} style={{ height: "100%" }}>
                  <tbody>
                    <tr>
                      <td
                        id="tdUserList"
                        style={{
                          border: "1px solid #CCC",
                          verticalAlign: "top",
                          height: pageMode === "AddUserForFirstTime" ? 180 : 280,
                        }}
                      >
                        <div id="divUserList" style={{ overflowY: "scroll", overflowX: "hidden", height: "100%" }}>
                          {/* mirrors: <%=UserList%> → fn_AddRow() → fn_SetSelRow(this) */}
                          <table width="90%" cellPadding={5} cellSpacing={0} border={0}
                            style={{ fontFamily: "Trebuchet MS", fontSize: "10pt", fontWeight: "normal", color: "black" }}>
                            <tbody>
                              {pageData.userList.map(row => (
                                <tr
                                  key={row.login}
                                  className={`user-row${selLogin === row.login ? " selected" : ""}`}
                                  onClick={() => fn_SetSelRow(row)}
                                >
                                  {/* mirrors: <td align=center nowrap style='width:10%;border-bottom:solid 1px #CCCCCC;'><img ... /></td> */}
                                  <td className="icon-cell">
                                    <img
                                      src={`/Images/${row.icon}`}
                                      alt={row.gender}
                                      style={{ width: 20, height: 20 }}
                                    />
                                  </td>
                                  {/* mirrors: <td align=left nowrap valign=middle style='padding-left:5px;width:90%;border-bottom:solid 1px #CCCCCC;'> */}
                                  <td className="name-cell">{row.fname}</td>
                                  {/* mirrors: <td style='display:none'>{login}</td> */}
                                  <td style={{ display: "none" }}>{row.login}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>

                    {/* Flash row — mirrors: trFlash (visible only for AddUserForFirstTime) */}
                    {showFlash && (
                      <tr style={{ height: 185 }}>
                        <td>
                          <div id="divFlash">
                            {/* mirrors: <img src='../Images/addnewuser_anim.png' /> */}
                            <img
                              src="/Images/addnewuser_anim.png"
                              alt="Add new user"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>

            {/* mirrors: <tr id="trManageUserButtons"> — visible for ManageUser/AddUser */}
            {showManageButtons && (
              <tr id="trManageUserButtons">
                <td width="75%" style={{ paddingTop: 15, textAlign: "center", verticalAlign: "top" }}>
                  {/* mirrors: imgSubmit1 */}
                  <button
                    type="submit"
                    className="btn-submit-img"
                    disabled={submitting}
                    title="Submit"
                  >
                    <img
                      src="/Images/submit_n.gif"
                      alt="Submit"
                      onMouseOver={e => (e.currentTarget.src = "/Images/submit_o.gif")}
                      onMouseOut={e  => (e.currentTarget.src = "/Images/submit_n.gif")}
                      onMouseDown={e => (e.currentTarget.src = "/Images/submit_d.gif")}
                    />
                  </button>
                  &nbsp;&nbsp;
                  {/* mirrors: imgClear1 */}
                  <button
                    type="button"
                    className="btn-clear-img"
                    onClick={fn_ClearFields}
                    title="Clear"
                  >
                    <img
                      src="/Images/clear_n.gif"
                      alt="Clear"
                      onMouseOver={e => (e.currentTarget.src = "/Images/clear_o.gif")}
                      onMouseOut={e  => (e.currentTarget.src = "/Images/clear_n.gif")}
                      onMouseDown={e => (e.currentTarget.src = "/Images/clear_d.gif")}
                    />
                  </button>
                </td>
                <td width="25%" style={{ height: 78 }}></td>
              </tr>
            )}

            {/* Continue button row — mirrors Master imgContinue (AddUserForFirstTime) */}
            {pageMode === "AddUserForFirstTime" && (
              <tr>
                <td colSpan={2} style={{ textAlign: "center", paddingTop: 10 }}>
                  <button
                    type="button"
                    onClick={fn_Continue}
                    style={{
                      background: "none", border: "none", cursor: "pointer", padding: 0,
                    }}
                    title="Continue"
                  >
                    <img
                      src="/Images/continue_n.gif"
                      alt="Continue"
                      onMouseOver={e => (e.currentTarget.src = "/Images/continue_o.gif")}
                      onMouseOut={e  => (e.currentTarget.src = "/Images/continue_n.gif")}
                    />
                  </button>
                </td>
              </tr>
            )}

          </tbody>
        </table>

        {/* mirrors: asp:Label ID="lblMesg" ForeColor="red" Style="display:none" */}
        <span
          id="lblMesg"
          style={{ display: "none", color: "red" }}
        />
      </form>
    </>
  );
}